import { Facebook, Instagram, Mail, Clock, Phone } from "lucide-react";

const GoogleIcon = ({ size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
  </svg>
);

const LINKS = [
  ["Services", "#services"],
  ["Our Work", "#proof"],
  ["Reviews", "#reviews"],
  ["Pricing", "#pricing"],
  ["FAQ", "#faq"],
  ["Contact", "#contact"],
];

const SOCIALS = [
  { icon: GoogleIcon, label: "Google", href: "https://www.google.com/search?q=Godspeed+Junk+Removal+%26+Hauling+North+Metro+Atlanta", slug: "google" },
  { icon: Facebook, label: "Facebook", href: "https://facebook.com/godspeedjunkhauling", slug: "facebook" },
  { icon: Instagram, label: "Instagram", href: "https://instagram.com/godspeedjunkhauling", slug: "instagram" },
];

const CITIES = "Acworth · Kennesaw · Marietta · Woodstock · Canton · Smyrna · Mableton · Powder Springs · Austell";

export const Footer = () => (
  <footer data-testid="site-footer" className="relative border-t-2 border-gold/40 bg-ink">
    <div className="hazard h-2.5 w-full" aria-hidden="true" />
    <div className="mx-auto max-w-7xl px-5 pb-28 pt-14 md:px-8 lg:pb-12">
      <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <img src="/assets/logo-new.png" alt="Godspeed Junk Removal & Hauling" data-testid="footer-logo" className="h-12 w-auto" />
          <p className="mt-5 max-w-sm text-sm capitalize leading-relaxed text-warmgray">
            GODSPEED Junk Hauling is a licensed and insured local junk removal company based in Kennesaw, GA —
            upfront pricing, same-day service, and a crew that sweeps up before it leaves.
          </p>
          <p className="mt-5 max-w-sm font-body text-xs font-bold uppercase leading-loose tracking-[0.15em] text-white/60">
            Serving: {CITIES}
          </p>
          <div className="mt-6 flex gap-3">
            {SOCIALS.map((s) => (
              <a
                key={s.slug}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Godspeed on ${s.label}`}
                data-testid={`social-link-${s.slug}`}
                className="flex h-10 w-10 items-center justify-center border-2 border-coal text-gold transition-all duration-200 hover:-translate-y-1 hover:border-gold"
              >
                <s.icon size={17} />
              </a>
            ))}
          </div>
        </div>
        <div className="lg:col-span-3">
          <p className="mb-5 font-body text-xs font-bold uppercase tracking-[0.25em] text-gold">Explore</p>
          <nav className="grid grid-cols-1 gap-3" aria-label="Footer">
            {LINKS.map(([label, href]) => (
              <a
                key={href}
                href={href}
                data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex w-fit items-center gap-2.5 font-heading text-xl uppercase tracking-wide text-white/75 transition-colors duration-200 hover:text-gold"
              >
                <span className="h-[3px] w-4 bg-gold/40 transition-all duration-200 group-hover:w-7 group-hover:bg-gold" aria-hidden="true" />
                {label}
              </a>
            ))}
          </nav>
        </div>
        <div className="lg:col-span-4">
          <p className="mb-5 font-body text-xs font-bold uppercase tracking-[0.25em] text-gold">Get Moving</p>
          <div className="space-y-3">
            <a href="tel:4706840150" data-testid="footer-phone-link" className="flex w-fit items-center gap-3 border-2 border-coal px-5 py-3 font-heading text-2xl uppercase tracking-wide text-white transition-colors duration-200 hover:border-gold hover:text-gold">
              <Phone size={18} className="text-gold" /> (470) 684-0150
            </a>
            <a href="mailto:info@godspeedjunkhauling.com" data-testid="footer-email-link" className="flex w-fit items-center gap-3 border-2 border-coal px-5 py-3 font-body text-sm font-bold text-white/85 transition-colors duration-200 hover:border-gold hover:text-gold">
              <Mail size={16} className="text-gold" /> info@godspeedjunkhauling.com
            </a>
            <p className="flex items-center gap-3 px-1 font-body text-sm font-bold uppercase tracking-[0.15em] text-white/70">
              <Clock size={16} className="text-gold" /> Mon–Sat · 9AM–7PM
            </p>
          </div>
          <a
            href="#contact"
            data-testid="footer-quote-button"
            className="btn-brutal shadow-hard-sm mt-7 inline-block bg-gold px-8 py-3.5 font-heading text-xl uppercase tracking-wide text-ink"
          >
            Get a Free Quote
          </a>
        </div>
      </div>
      <div className="mt-14 flex flex-col items-center gap-4 border-t-2 border-coal pt-8 md:flex-row md:justify-between">
        <p data-testid="footer-copyright" className="font-body text-xs font-bold uppercase tracking-[0.15em] text-white/50">
          © 2026 Godspeed Junk Removal & Hauling, LLC
        </p>
        <p className="font-body text-xs font-bold uppercase tracking-[0.15em] text-white/50">
          Licensed & Insured · Locally Owned · Same-Day
        </p>
        <p className="font-scripture text-base italic capitalize text-gold/90">
          Built on faith. Built to last.
        </p>
      </div>
    </div>
  </footer>
);
